import type {
  ConfigureModelLayers,
  DatasetDescriptor,
  FormulaDescriptor,
  RelationshipDatasetInfo,
  SummaryVisible,
  TableConfig,
  TableLayout
} from "@kaleidoscope-solutions/engine-wasm"
import type {
  Cell
} from "@/logic/GridBuilder"
import type { Snackbar } from "@/utils/useSnackbar"

import {
  CellKind,
  Disjoint,
  LayoutElement,
  TableType,
} from "@kaleidoscope-solutions/engine-wasm"
import { captureException, setContext, setTag } from "@sentry/vue"
import { useSettingsStore } from "@/stores"
import { listDiff } from "@/utils"
import { ApplicationError, ErrorCode } from "@/utils/ApplicationError"
import { invalidateContentModelIndex } from "@/utils/contentModelIndex"

import { EngineError } from "@/utils/useEngineModel/error"
import { ModelApi } from "@/utils/useEngineModel/ModelApi"
import { useSnackbar } from "@/utils/useSnackbar"

const { addSnackbar } = useSnackbar()

export type DatasetType = "Text" | "Number" | "Boolean" | "List" | "Calculation"

export type InspectTargetKind = "Type" | "Dataset" | "List" | "Name"

export type RelationshipGraphTargetKindApi = "Type" | "List" | "Dataset"

export type RelationshipDirection = "Inbound" | "Outbound" | "Unknown"

export type RelationshipCategory = "date" | "nonDate"

export type MatchKind = "exact" | "includes" | "inflection"

export type ResolveReasonCode =
  | "exact-name" |
  "type-match" |
  "dataset-match" |
  "view-match" |
  "page-match" |
  "fallback" |
  "no-match"

export interface ApiRevisionInfo {
  modelRevision: string
  relationshipsRevision: string
  tableRevision?: string
}

export interface ApiTimingDiagnostics {
  elapsedMs?: number
  buildMs?: number
  serializeMs?: number
  totalMs?: number
}

export interface RelationshipGraphTargetApi {
  kind: RelationshipGraphTargetKindApi
  identifier: string
}

export interface RelationshipGraphOptionsApi {
  maxHops?: number
  includeNodeMeta?: boolean
  includeCounts?: boolean
  includeDisplayLabels?: boolean
  includeResolvedViews?: boolean
  classifyDateEdges?: boolean
  separateByCategory?: boolean
  includeWarnings?: boolean
  includeDiagnostics?: boolean
  includeFields?: RelationshipGraphFieldApi[]
}

export type RelationshipGraphFieldApi =
  | "root" |
  "nodes" |
  "edges" |
  "frontierByHop" |
  "categoryEdges" |
  "warnings" |
  "diagnostics" |
  "revisions"

export interface RelationshipGraphResultApi {
  root: RelationshipGraphNodeApi
  nodes: RelationshipGraphNodeApi[]
  edges: RelationshipGraphEdgeApi[]
  frontierByHop: string[][]
  categoryEdges?: RelationshipGraphCategoryEdgesApi
  truncated: boolean
  warnings: string[]
  revisions?: ApiRevisionInfo
  diagnostics?: ApiTimingDiagnostics
}

export interface RelationshipGraphCategoryEdgesApi {
  date: RelationshipGraphEdgeApi[]
  nonDate: RelationshipGraphEdgeApi[]
}

export interface RelationshipGraphNodeApi {
  id: string
  label: string
  kind: string
  distance: number
  displayLabel?: string
  canonicalName?: string
  primaryListName?: string
  previewViewName?: string
  itemCount?: number
  isDateFamily?: boolean
}

export interface RelationshipGraphEdgeApi {
  id: string
  from: string
  to: string
  datasetName?: string
  relationshipKind?: string
  inverseName?: string
  userDefined?: boolean
  directionFromRoot?: RelationshipDirection
  category?: RelationshipCategory
  displayFromLabel?: string
  displayToLabel?: string
}

export interface GridAnalysisRequest {
  viewName: string
  includeSummaryCells?: boolean
  includeFields?: GridAnalysisField[]
  includeDiagnostics?: boolean
}

export type GridAnalysisField =
  | "totalCells" |
  "readOnlyCount" |
  "cellKindCounts" |
  "displayKindCounts" |
  "valueTypeCounts" |
  "stats" |
  "summaryCells" |
  "diagnostics" |
  "revisions"

export interface GridAnalysisResult {
  viewName: string
  totalCells: number
  readOnlyCount: number
  cellKindCounts: Record<string, number>
  displayKindCounts: Record<string, number>
  valueTypeCounts: Record<string, number>
  stats: GridStats
  summaryCells?: GridSummaryCell[]
  revisions?: ApiRevisionInfo
  diagnostics?: ApiTimingDiagnostics
}

export interface GridStats {
  sortableCells: number
  expandableCells: number
  iconCells: number
  detailPageCells: number
  spannedCells: number
  hierarchyCells: number
  maxHierarchyLevel: number
}

export interface GridSummaryCell {
  row: number
  column: number
  formattedValue: string
  valueType: string
  cellKindLabel: string
  readOnly: boolean
}

export interface BatchListItemCountsRequest {
  typeNames: string[]
  includeUnresolved?: boolean
}

export interface BatchListItemCountsResponse {
  countsByTypeName: Record<string, number>
  unresolved?: BatchResolveError[]
  revisions?: ApiRevisionInfo
}

export interface BatchResolvePreviewViewNamesRequest {
  typeNames: string[]
  includeUnresolved?: boolean
}

export interface BatchResolvePreviewViewNamesResponse {
  previewViewNameByTypeName: Record<string, string>
  unresolved?: BatchResolveError[]
  revisions?: ApiRevisionInfo
}

export interface BatchResolveError {
  input: string
  reasonCode: ResolveReasonCode
}

export interface ResolveInspectTargetsRequest {
  inputs: ResolveInspectTargetInput[]
  includeAlternatives?: boolean
}

export interface ResolveInspectTargetInput {
  kind: InspectTargetKind
  value: string
  context?: ResolveInspectContext
}

export interface ResolveInspectContext {
  sourceViewName?: string
  sourcePageName?: string
  preferNonDetailPages?: boolean
}

export interface ResolveInspectTargetsResponse {
  results: ResolveInspectTargetResult[]
  revisions?: ApiRevisionInfo
}

export interface ResolveInspectTargetResult {
  input: ResolveInspectTargetInput
  bestMatch?: ResolvedInspectTarget
  alternatives?: ResolvedInspectTarget[]
  unresolved?: BatchResolveError
}

export interface ResolvedInspectTarget {
  confidence: number
  reasonCode: ResolveReasonCode
  matchedBy: MatchKind[]
  target: {
    pageName?: string
    pageSectionId?: string
    viewName?: string
    previewViewName?: string
    kind: "page" | "view" | "list" | "dataset"
  }
}

export interface InspectPanelSnapshotRequest {
  viewName: string
  relationshipRoot?: {
    kind: RelationshipGraphTargetKindApi
    identifier: string
  }
  includeFields?: InspectPanelSnapshotField[]
  maxRelationshipHops?: number
  includeDiagnostics?: boolean
}

export type InspectPanelSnapshotField =
  | "gridAnalysis" |
  "relationshipGraph" |
  "resolvedTargets" |
  "listItemCounts" |
  "tableHeaderMetadata" |
  "diagnostics" |
  "revisions"

export interface InspectPanelSnapshotResponse {
  viewName: string
  gridAnalysis?: GridAnalysisResult
  relationshipGraph?: RelationshipGraphResultApi
  resolvedTargets?: ResolveInspectTargetsResponse
  listItemCountsByTypeName?: Record<string, number>
  tableHeaderMetadata?: InspectTableHeaderMetadata
  revisions?: ApiRevisionInfo
  diagnostics?: ApiTimingDiagnostics
}

export interface InspectTableHeaderMetadata {
  formula?: string | null
  nativeDimensions: string[]
  isHomeTable: boolean
  homeTableName?: string | null
}

export interface RelationshipGraphTarget {
  kind: "Type" | "List" | "Dataset"
  identifier: string
}

export interface RelationshipGraphOptions {
  maxHops?: number
}

export interface RelationshipGraphNode {
  id: string
  label: string
  kind: string
  distance: number
}

export interface RelationshipGraphEdge {
  id: string
  from: string
  to: string
  datasetName?: string
  relationshipKind?: string
  inverseName?: string
  userDefined?: boolean
  directionFromRoot?: "Inbound" | "Outbound" | "Unknown"
}

export interface RelationshipGraphResult {
  root: RelationshipGraphNode
  nodes: RelationshipGraphNode[]
  edges: RelationshipGraphEdge[]
  frontierByHop: string[][]
  truncated: boolean
  warnings: string[]
}

type snackbarClosure = (error: EngineError) => Snackbar
interface captureOptions {
  snackbar?: boolean | string | snackbarClosure
  rethrow?: boolean
  unhandledRejection?: boolean
}

export enum HandledError {
  Yes,
  No
}

function isCancellationLikeError(error: unknown): boolean {
  if (typeof error === "string") {
    return error.toLowerCase().includes("cancel")
  }

  if (!error || typeof error !== "object") {
    return false
  }

  const candidate = error as { name?: string; message?: string; code?: string }
  const name = String(candidate.name ?? "").toLowerCase()
  const message = String(candidate.message ?? "").toLowerCase()
  const code = String(candidate.code ?? "").toLowerCase()
  return name.includes("cancel") || message.includes("cancel") || code.includes("cancel")
}

export function captureError(error: any, options?: captureOptions): HandledError {
  if (options?.rethrow) {
    throw error
  }

  if (isCancellationLikeError(error)) {
    return HandledError.Yes
  }

  if (!(error instanceof EngineError)) {
    console.error(error)
    return HandledError.No
  }

  const snackbar = options?.snackbar
  if (snackbar && typeof snackbar === "boolean") {
    addSnackbar({
      type: "error",
      message: `${error.messageWithoutCode}`,
    })
  }

  if (snackbar && typeof snackbar === "string") {
    addSnackbar({
      type: "error",
      message: snackbar
    })
  }

  if (snackbar && typeof snackbar === "function") {
    addSnackbar(snackbar(error))
  }

  if (!(error instanceof EngineError)) {
    console.error(error)
    return HandledError.No
  }

  console.error(`${error.code}: ${error.message}`)

  for (const [key, value] of Object.entries(error.context)) {
    setContext(key, { value })
  }

  for (const [key, value] of Object.entries(error.tags)) {
    setTag(key, value)
  }

  if (options?.unhandledRejection) {
    setTag("handled", "no")
  }

  captureException(error)

  return HandledError.Yes
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object"
}

function isString(value: unknown): value is string {
  return typeof value === "string"
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value)
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean"
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isString)
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return isRecord(value) && Object.values(value).every(isString)
}

function isNumberRecord(value: unknown): value is Record<string, number> {
  return isRecord(value) && Object.values(value).every(isFiniteNumber)
}

function isApiRevisionInfo(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  return isString(value.modelRevision) &&
    isString(value.relationshipsRevision) &&
    (value.tableRevision === undefined || isString(value.tableRevision))
}

function isApiTimingDiagnostics(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  return (value.elapsedMs === undefined || isFiniteNumber(value.elapsedMs)) &&
    (value.buildMs === undefined || isFiniteNumber(value.buildMs)) &&
    (value.serializeMs === undefined || isFiniteNumber(value.serializeMs)) &&
    (value.totalMs === undefined || isFiniteNumber(value.totalMs))
}

function isGridStats(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  return isFiniteNumber(value.sortableCells) &&
    isFiniteNumber(value.expandableCells) &&
    isFiniteNumber(value.iconCells) &&
    isFiniteNumber(value.detailPageCells) &&
    isFiniteNumber(value.spannedCells) &&
    isFiniteNumber(value.hierarchyCells) &&
    isFiniteNumber(value.maxHierarchyLevel)
}

function isGridSummaryCell(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  return isFiniteNumber(value.row) &&
    isFiniteNumber(value.column) &&
    isString(value.formattedValue) &&
    isString(value.valueType) &&
    isString(value.cellKindLabel) &&
    isBoolean(value.readOnly)
}

function isResolveReasonCode(value: unknown): boolean {
  return value === "exact-name" ||
    value === "type-match" ||
    value === "dataset-match" ||
    value === "view-match" ||
    value === "page-match" ||
    value === "fallback" ||
    value === "no-match"
}

function isBatchResolveError(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  return isString(value.input) && isResolveReasonCode(value.reasonCode)
}

function isRelationshipDirection(value: unknown): boolean {
  return value === "Inbound" || value === "Outbound" || value === "Unknown"
}

function isRelationshipCategory(value: unknown): boolean {
  return value === "date" || value === "nonDate"
}

function isRelationshipNode(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  return isString(value.id) &&
    isString(value.label) &&
    isString(value.kind) &&
    isFiniteNumber(value.distance) &&
    (value.displayLabel === undefined || isString(value.displayLabel)) &&
    (value.canonicalName === undefined || isString(value.canonicalName)) &&
    (value.primaryListName === undefined || isString(value.primaryListName)) &&
    (value.previewViewName === undefined || isString(value.previewViewName)) &&
    (value.itemCount === undefined || isFiniteNumber(value.itemCount)) &&
    (value.isDateFamily === undefined || isBoolean(value.isDateFamily))
}

function isRelationshipEdge(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  return isString(value.id) &&
    isString(value.from) &&
    isString(value.to) &&
    (value.datasetName === undefined || isString(value.datasetName)) &&
    (value.relationshipKind === undefined || isString(value.relationshipKind)) &&
    (value.inverseName === undefined || isString(value.inverseName)) &&
    (value.userDefined === undefined || isBoolean(value.userDefined)) &&
    (value.directionFromRoot === undefined || isRelationshipDirection(value.directionFromRoot)) &&
    (value.category === undefined || isRelationshipCategory(value.category)) &&
    (value.displayFromLabel === undefined || isString(value.displayFromLabel)) &&
    (value.displayToLabel === undefined || isString(value.displayToLabel))
}

function isRelationshipCategoryEdges(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  return Array.isArray(value.date) &&
    value.date.every(isRelationshipEdge) &&
    Array.isArray(value.nonDate) &&
    value.nonDate.every(isRelationshipEdge)
}

function isInspectTargetKind(value: unknown): boolean {
  return value === "Type" || value === "Dataset" || value === "List" || value === "Name"
}

function isResolveInspectContext(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  return (value.sourceViewName === undefined || isString(value.sourceViewName)) &&
    (value.sourcePageName === undefined || isString(value.sourcePageName)) &&
    (value.preferNonDetailPages === undefined || isBoolean(value.preferNonDetailPages))
}

function isResolveInspectTargetInput(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  return isInspectTargetKind(value.kind) &&
    isString(value.value) &&
    (value.context === undefined || isResolveInspectContext(value.context))
}

function isMatchKind(value: unknown): boolean {
  return value === "exact" || value === "includes" || value === "inflection"
}

function isResolvedTargetKind(value: unknown): boolean {
  return value === "page" || value === "view" || value === "list" || value === "dataset"
}

function isResolvedInspectTarget(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  const target = value.target
  if (!isRecord(target)) {
    return false
  }
  return isFiniteNumber(value.confidence) &&
    isResolveReasonCode(value.reasonCode) &&
    Array.isArray(value.matchedBy) &&
    value.matchedBy.every(isMatchKind) &&
    isResolvedTargetKind(target.kind) &&
    (target.pageName === undefined || isString(target.pageName)) &&
    (target.pageSectionId === undefined || isString(target.pageSectionId)) &&
    (target.viewName === undefined || isString(target.viewName)) &&
    (target.previewViewName === undefined || isString(target.previewViewName))
}

function isResolveInspectTargetResult(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  return isResolveInspectTargetInput(value.input) &&
    (value.bestMatch === undefined || isResolvedInspectTarget(value.bestMatch)) &&
    (value.alternatives === undefined ||
      (Array.isArray(value.alternatives) && value.alternatives.every(isResolvedInspectTarget))) &&
      (value.unresolved === undefined || isBatchResolveError(value.unresolved))
}

function isInspectTableHeaderMetadata(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }
  return (value.formula === undefined || value.formula === null || isString(value.formula)) &&
    isStringArray(value.nativeDimensions) &&
    isBoolean(value.isHomeTable) &&
    (value.homeTableName === undefined || value.homeTableName === null || isString(value.homeTableName))
}

function logInvalidPayload(endpoint: string, requestContext: unknown, payload: unknown) {
  console.error(`[ModelFacade] ${endpoint} returned invalid payload`, {
    endpoint,
    requestContext,
    payload,
  })
}

function parseApiPayload<T>(model: ModelFacade, value: unknown): T | undefined {
  if (typeof value === "string") {
    return model.parseResult<T>(value)
  }
  return value as T
}

function isGridAnalysisResult(value: unknown): value is GridAnalysisResult {
  if (!isRecord(value)) {
    return false
  }
  return typeof value.viewName === "string" &&
    isFiniteNumber(value.totalCells) &&
    isFiniteNumber(value.readOnlyCount) &&
    isNumberRecord(value.cellKindCounts) &&
    isNumberRecord(value.displayKindCounts) &&
    isNumberRecord(value.valueTypeCounts) &&
    isGridStats(value.stats) &&
    (value.summaryCells === undefined ||
      (Array.isArray(value.summaryCells) && value.summaryCells.every(isGridSummaryCell))) &&
      (value.revisions === undefined || isApiRevisionInfo(value.revisions)) &&
      (value.diagnostics === undefined || isApiTimingDiagnostics(value.diagnostics))
}

function isRelationshipGraphResult(value: unknown): value is RelationshipGraphResultApi {
  if (!isRecord(value)) {
    return false
  }
  return isRelationshipNode(value.root) &&
    Array.isArray(value.nodes) &&
    value.nodes.every(isRelationshipNode) &&
    Array.isArray(value.edges) &&
    value.edges.every(isRelationshipEdge) &&
    Array.isArray(value.frontierByHop) &&
    value.frontierByHop.every(isStringArray) &&
    isBoolean(value.truncated) &&
    isStringArray(value.warnings) &&
    (value.categoryEdges === undefined || isRelationshipCategoryEdges(value.categoryEdges)) &&
    (value.revisions === undefined || isApiRevisionInfo(value.revisions)) &&
    (value.diagnostics === undefined || isApiTimingDiagnostics(value.diagnostics))
}

function isBatchListItemCountsResponse(value: unknown): value is BatchListItemCountsResponse {
  return isRecord(value) &&
    isNumberRecord(value.countsByTypeName) &&
    (value.unresolved === undefined ||
      (Array.isArray(value.unresolved) && value.unresolved.every(isBatchResolveError))) &&
      (value.revisions === undefined || isApiRevisionInfo(value.revisions))
}

function isBatchResolvePreviewViewNamesResponse(value: unknown): value is BatchResolvePreviewViewNamesResponse {
  return isRecord(value) &&
    isStringRecord(value.previewViewNameByTypeName) &&
    (value.unresolved === undefined ||
      (Array.isArray(value.unresolved) && value.unresolved.every(isBatchResolveError))) &&
      (value.revisions === undefined || isApiRevisionInfo(value.revisions))
}

function isResolveInspectTargetsResponse(value: unknown): value is ResolveInspectTargetsResponse {
  return isRecord(value) &&
    Array.isArray(value.results) &&
    value.results.every(isResolveInspectTargetResult) &&
    (value.revisions === undefined || isApiRevisionInfo(value.revisions))
}

function isInspectPanelSnapshotResponse(value: unknown): value is InspectPanelSnapshotResponse {
  return isRecord(value) &&
    isString(value.viewName) &&
    (value.gridAnalysis === undefined || isGridAnalysisResult(value.gridAnalysis)) &&
    (value.relationshipGraph === undefined || isRelationshipGraphResult(value.relationshipGraph)) &&
    (value.resolvedTargets === undefined || isResolveInspectTargetsResponse(value.resolvedTargets)) &&
    (value.listItemCountsByTypeName === undefined || isNumberRecord(value.listItemCountsByTypeName)) &&
    (value.tableHeaderMetadata === undefined || isInspectTableHeaderMetadata(value.tableHeaderMetadata)) &&
    (value.revisions === undefined || isApiRevisionInfo(value.revisions)) &&
    (value.diagnostics === undefined || isApiTimingDiagnostics(value.diagnostics))
}

export class ModelFacade extends ModelApi {
  private viewsPromise: Promise<Map<string, UiViewDescriptor>> | null = null
  private formulasPromise: Promise<FormulaDescriptor[]> | null = null

  private invalidateViewsCache() {
    this.viewsPromise = null
    this.formulasPromise = null
  }

  private async callStrictInspectEndpoint<T>(
    endpoint: string,
    requestContext: unknown,
    apiMethod: unknown,
    invoke: (method: (...args: any[]) => Promise<unknown>) => Promise<unknown>,
    validate: (value: unknown) => value is T
  ): Promise<T> {
    if (typeof apiMethod !== "function") {
      throw new TypeError(`[ModelFacade] Missing inspect API endpoint: ${endpoint}`)
    }

    let rawResult: unknown
    try {
      rawResult = await invoke(apiMethod as (...args: any[]) => Promise<unknown>)
    } catch (error: any) {
      captureError(error)
      throw error
    }

    const parsed = parseApiPayload<T>(this, rawResult)
    if (!validate(parsed)) {
      logInvalidPayload(endpoint, requestContext, parsed)
      throw new Error(`[ModelFacade] ${endpoint} returned invalid payload`)
    }
    return parsed
  }

  override async initialise(
    tokenCallback: () => Promise<string>,
    workerPath?: string,
    enableMessagePoll = true
  ) {
    this.invalidateViewsCache()
    await super.initialise(tokenCallback, workerPath, enableMessagePoll)
  }

  /**
   * View Methods
   */
  async updateTableLayout(
    viewName: string,
    contextElements: LayoutElement[],
    rowElements: LayoutElement[],
    columnElements: LayoutElement[],
    rowGroups: LayoutElement[],
    columnGroups: LayoutElement[]
  ) {
    contextElements = contextElements.map(this.cloneLayoutElement)
    rowElements = rowElements.map(this.cloneLayoutElement)
    columnElements = columnElements.map(this.cloneLayoutElement)
    rowGroups = rowGroups.map(this.cloneLayoutElement)
    columnGroups = columnGroups.map(this.cloneLayoutElement)

    try {
      const updatedLayout = await this.api?.updateTableLayout(
        viewName,
        contextElements,
        rowElements,
        columnElements,
        rowGroups,
        // Column groups are ordered depth-first-post ordering, so the outer groups come last.
        // The engine uses semantic ordering for groups (always outer to inner). This essentially
        // means the order on screen for columns is the reverse of what the engine uses so flip the
        // visual layout order so outer groups are first in the array and inner groups are last.
        columnGroups.reverse()
      )
      this.invalidateViewsCache()
      return updatedLayout
    } catch (error: any) {
      captureError(error, { rethrow: true })
    }
  }

  async addRowElementToTableLayout(viewName: string, elementName: string) {
    try {
      await this.api?.addRowElementToTableLayout(viewName, elementName)
      this.invalidateViewsCache()
    } catch (error: any) {
      captureError(error, { snackbar: true })
    }
  }

  async addColumnElementToTableLayout(viewName: string, elementName: string) {
    try {
      await this.api?.addColumnElementToTableLayout(viewName, elementName)
      this.invalidateViewsCache()
    } catch (error: any) {
      captureError(error, { snackbar: true })
    }
  }

  async createView(name: string, rowList: string, colList: string) {
    try {
      await this.api?.createView(name, rowList, colList)
      this.invalidateViewsCache()
    } catch (error: any) {
      captureError(error, { snackbar: true })
      throw error
    }
  }

  async addTableToPage(projectName: string, pageName: string, viewName: string) {
    try {
      await this.api?.addTableToPage(projectName, pageName, viewName)
    } catch (error: any) {
      captureError(error, { snackbar: true })
      throw error
    }
  }

  async removeColumnElementFromTableLayout(
    viewName: string,
    elementName: string
  ) {
    try {
      await this.api?.removeColumnElementFromTableLayout(viewName, elementName)
      this.invalidateViewsCache()
    } catch (error: any) {
      captureError(error, { snackbar: true })
    }
  }

  async removeColumnElementFromTableLayoutByCell(
    tableName: string,
    cell: Cell
  ) {
    const cellObjectId = await this.api.getCellObjectIdentifier(
      tableName,
      cell.id
    )
    return this.removeColumnElementFromTableLayout(tableName, cellObjectId)
  }

  async removeRowElementFromTableLayout(viewName: string, elementName: string) {
    try {
      await this.api?.removeRowElementFromTableLayout(viewName, elementName)
      this.invalidateViewsCache()
    } catch (error: any) {
      captureError(error, { snackbar: true })
    }
  }

  async setTableTotals(
    viewName: string,
    summaryVisible: SummaryVisible
  ) {
    this.invalidateViewsCache()
    await this.api.setSummaryVisibleOnTable(viewName, summaryVisible)
  }

  /*
   * Type Methods
   */
  async primaryListNameForType(typeName: string) {
    const listName = await this.api.primaryListNameForType(typeName)
    if (listName) {
      return listName
    }
    const list = await this.api.primaryListForType(typeName)
    return list.name
  }

  async listItemCountForType(typeName: string): Promise<number> {
    return await this.api.listItemCountForType(typeName)
  }

  async listNames(): Promise<string[]> {
    return await this.api.listNames()
  }

  async datasetNamesForType(typeName: string): Promise<string[]> {
    return await this.api.datasetNamesForType(typeName)
  }

  async relationshipDatasetsForType(typeName: string): Promise<RelationshipDatasetInfo[]> {
    return await this.api.relationshipDatasetsForType(typeName)
  }

  async gridAnalysis(
    request: GridAnalysisRequest
  ): Promise<GridAnalysisResult> {
    return await this.callStrictInspectEndpoint<GridAnalysisResult>(
      "gridAnalysis",
      { request },
      (this.api as any)?.gridAnalysis,
      async (apiMethod) => await apiMethod.call(this.api, request),
      isGridAnalysisResult
    )
  }

  async relationshipGraph(
    target: RelationshipGraphTargetApi,
    options: RelationshipGraphOptionsApi = {}
  ): Promise<RelationshipGraphResultApi> {
    return await this.callStrictInspectEndpoint<RelationshipGraphResultApi>(
      "relationshipGraph",
      { target, options },
      (this.api as any)?.relationshipGraph,
      async (apiMethod) => await apiMethod.call(this.api, target, options),
      isRelationshipGraphResult
    )
  }

  async listItemCountsForTypes(
    request: BatchListItemCountsRequest
  ): Promise<BatchListItemCountsResponse> {
    return await this.callStrictInspectEndpoint<BatchListItemCountsResponse>(
      "listItemCountsForTypes",
      { request },
      (this.api as any)?.listItemCountsForTypes,
      async (apiMethod) => await apiMethod.call(this.api, request),
      isBatchListItemCountsResponse
    )
  }

  async previewViewNamesForTypes(
    request: BatchResolvePreviewViewNamesRequest
  ): Promise<BatchResolvePreviewViewNamesResponse> {
    return await this.callStrictInspectEndpoint<BatchResolvePreviewViewNamesResponse>(
      "previewViewNamesForTypes",
      { request },
      (this.api as any)?.previewViewNamesForTypes,
      async (apiMethod) => await apiMethod.call(this.api, request),
      isBatchResolvePreviewViewNamesResponse
    )
  }

  async resolveInspectTargets(
    request: ResolveInspectTargetsRequest
  ): Promise<ResolveInspectTargetsResponse> {
    return await this.callStrictInspectEndpoint<ResolveInspectTargetsResponse>(
      "resolveInspectTargets",
      { request },
      (this.api as any)?.resolveInspectTargets,
      async (apiMethod) => await apiMethod.call(this.api, request),
      isResolveInspectTargetsResponse
    )
  }

  async inspectPanelSnapshot(
    request: InspectPanelSnapshotRequest
  ): Promise<InspectPanelSnapshotResponse> {
    return await this.callStrictInspectEndpoint<InspectPanelSnapshotResponse>(
      "inspectPanelSnapshot",
      { request },
      (this.api as any)?.inspectPanelSnapshot,
      async (apiMethod) => await apiMethod.call(this.api, request),
      isInspectPanelSnapshotResponse
    )
  }

  /*
   * List Methods
   */

  async renameEntityByCellId(tableName: string, cell: Cell, value: string) {
    const entityTypeName = await this.api.getCellEntityType(tableName, cell.id)
    await this.api.renameEntity(entityTypeName, cell.formattedValue, value)
  }

  async renameDataset(tableName: string, cell: Cell, value: string) {
    const cellObjectId = await this.api.getCellObjectIdentifier(
      tableName,
      cell.id
    )

    await this.api.renameDataset(cellObjectId, value)
  }

  async deleteEntityByCellId(tableName: string, cellId: string) {
    const entityTypeName = await this.api.getCellEntityType(tableName, cellId)
    const cellObjectId = await this.api.getCellObjectIdentifier(
      tableName,
      cellId
    )

    return this.api.deleteEntity(entityTypeName, cellObjectId)
  }

  async getListItemsByCellId(tableName: string, cellId: string) {
    const entityTypeName = await this.api.getCellEntityType(tableName, cellId)

    if (entityTypeName === "") {
      throw new Error("type not found")
    }

    return await this.listItemsFromType(entityTypeName)
  }

  async addNewItemToListByCellId(
    tableName: string,
    cellId: string,
    value: string
  ) {
    const entityTypeName = await this.api.getCellEntityType(tableName, cellId)

    if (entityTypeName === "") {
      throw new Error("type not found")
    }

    return await this.addNewItemToList(entityTypeName, value)
  }

  async listItemsFromType(typeName: string): Promise<string[]> {
    const listName = await this.primaryListNameForType(typeName)
    return await this.api.listItems(listName)
  }

  async addNewItemToList(typeName: string, value?: string) {
    if (!typeName) {
      throw new Error("type not found")
    }

    const listName = await this.primaryListNameForType(typeName)

    if (value === undefined || value === "") {
      await this.api.addDefaultItemToList(listName)
    } else {
      await this.api.addItemsToList(listName, [value])
    }

    const listItems = await this.api.listItems(listName)
    // Get the last item added to the list.
    return listItems[listItems.length - 1]
  }

  async getCellFullPrecisionNumber(tableName: string, cellId: string) {
    return await this.api.getCellFullPrecisionNumber(tableName, cellId)
  }

  updateItemsFromDatasetListValue(
    tableName: string,
    cellId: string,
    originalValue: string,
    cellValue: string[]
  ): Promise<void> {
    return this.fireAndForgetSave(async () => {
      const cellDatasetName = await this.api.getCellDatasetKey(
        tableName,
        cellId
      )

      const datasetName: string = cellDatasetName.name as string
      const datasetKeyTypes = cellDatasetName.keyTypes as string[]
      const datasetKeys = cellDatasetName.keys as string[]

      const before: string[] = originalValue
        ? originalValue.split(",").map((v: string) => v.trim())
        : []
      const [added, removed] = listDiff(before, cellValue)

      for (const removedValue of removed) {
        await this.api.removeItemFromDatasetListValue(
          datasetName,
          datasetKeyTypes,
          datasetKeys,
          removedValue
        )
      }

      for (const addedValue of added) {
        await this.api.setDatasetValue(
          datasetName,
          datasetKeyTypes,
          datasetKeys,
          addedValue
        )
      }
    })
  }

  filterDatasetsOnViews(
    tableLayout: TableLayout,
    dataset: DatasetDescriptor
  ): boolean {
    const inColumns =
      tableLayout.columnElements.filter(
        (ce: LayoutElement) => ce.name === dataset.name
      ) ?? []
    const inRows =
      tableLayout.rowElements.filter(
        (re: LayoutElement) => re.name === dataset.name
      ) ?? []
    return !(inColumns.length > 0 || inRows.length > 0)
  }

  async cellIsRelationshipName(tableName: string, cell: Cell) {
    try {
      const cellObjectId = await this.api.getCellObjectIdentifier(
        tableName,
        cell.id
      )

      if (cell.cellKind !== CellKind.DatasetName) {
        return false
      }

      const dataset = await this.api.dataset(cellObjectId)

      return dataset.relationship !== undefined
    } catch {
      return false
    }
  }

  async getImportTargets(): Promise<{ datasets: string[]; categories: string[] }> {
    const { datasets, categories } = await this.api.getImportTargets()
    return { datasets: datasets.sort(), categories: categories.sort() }
  }

  defaultAttributes(listName: string): Promise<DatasetDescriptor[]> {
    return this.api.defaultAttributes(listName)
  }

  cloneLayoutElement(element: LayoutElement) {
    const clonedElement = new LayoutElement()
    clonedElement.name = element.name
    clonedElement.label = element.label
    clonedElement.elementKind = element.elementKind
    return clonedElement
  }

  async fireAndForgetSave<T>(logic: () => Promise<T> | T): Promise<T> {
    const logicReturn = await logic()
    void this.api.saveModel(Disjoint.No)
    return logicReturn
  }

  async save<T>(logic: () => Promise<T> | T): Promise<T> {
    const logicReturn = await logic()
    await this.api.saveModel(Disjoint.No)
    return logicReturn
  }

  async disjointSave<T>(logic: () => Promise<T> | T): Promise<T> {
    const logicReturn = await logic()
    await this.api.saveModel(Disjoint.Yes)
    return logicReturn
  }

  async loadModel() {
    try {
      this.invalidateViewsCache()
      await this.api.loadModel()
    } catch (error: any) {
      captureError(error)
      throw new ApplicationError(ErrorCode.E9000)
    }
  }

  async configureModelAndReload(layers: ConfigureModelLayers) {
    try {
      this.invalidateViewsCache()
      invalidateContentModelIndex(this)
      await this.api.configureRemoteModel(layers)

      const settingsStore = useSettingsStore()
      settingsStore.resetBannerList()

      window.location.href = "/"

      return { success: true }
    } catch (error: any) {
      captureError(error)
      throw error
    }
  }

  async datasetNames(): Promise<string[]> {
    const rawDatasets = await this.api.evaluate("Datasets")
    const parsedDatasets = (JSON.parse(rawDatasets) as { rows: any[] }).rows
    const datasetStrings = parsedDatasets.flat()

    return datasetStrings
  }

  async datasets(): Promise<DatasetDescriptor[]> {
    const datasetStrings = await this.datasetNames()

    const datasetDescriptors = await Promise.all(
      datasetStrings.map(async (datasetName: string) => {
        const dataset = await this.api.dataset(datasetName)
        return dataset
      })
    )

    return datasetDescriptors
  }

  async viewNames(): Promise<string[]> {
    const rawViews = await this.api.evaluate("Views")
    const parsedViews = (JSON.parse(rawViews) as { rows: any[] }).rows
    const viewStrings = parsedViews.flat()

    return viewStrings
  }

  async views(): Promise<Map<string, UiViewDescriptor>> {
    if (!this.viewsPromise) {
      this.viewsPromise = Promise.all([this.viewNames(), this.formulas()])
        .then(async ([viewStrings, formulas]) => {
          const formulaByName = new Map(
            formulas.map((formula) => [formula.name, formula.formula])
          )

          const views: UiViewDescriptor[] = await Promise.all(
            viewStrings.map(async (viewName: string) => {
              const table = await this.api.table(viewName)
              return this.buildViewDescriptor(viewName, table, formulaByName)
            })
          )

          return new Map(views.map((view: UiViewDescriptor) => [view.name, view]))
        })
        .catch((error) => {
          this.invalidateViewsCache()
          throw error
        })
    }

    return await this.viewsPromise
  }

  private buildViewDescriptor(
    viewName: string,
    table: { layout: TableLayout; config: TableConfig },
    formulaByName: Map<string, string>
  ): UiViewDescriptor {
    const { layout, config } = table
    const layoutElements = [
      ...(layout.rowElements as LayoutElement[]),
      ...(layout.columnElements as LayoutElement[]),
      ...(layout.contextElements as LayoutElement[]),
      ...(layout.rowGroups as LayoutElement[]),
      ...(layout.columnGroups as LayoutElement[]),
    ]
    const datasetNames = new Set(
      layoutElements.filter((element) => element.isDataset).map((element) => element.name)
    )
    const listNames = new Set(
      layoutElements.filter((element) => element.isList).map((element) => element.name)
    )

    let homeTable: string | null = null

    if (config.tableType === TableType.Block) {
      const [datasetName] = [...datasetNames]
      if (datasetNames.size === 1 && datasetName === viewName) {
        homeTable = datasetName
      }
    }

    if (config.tableType === TableType.List) {
      const [listName] = [...listNames]
      if (listNames.size === 1 && `${listName}` === `${viewName}::All`) {
        homeTable = listName
      }
    }

    return {
      name: viewName,
      layout,
      config,
      homeTable,
      formula: formulaByName.get(viewName),
      customView: homeTable === null,
    }
  }

  async view(viewName: string, prefetchedTable?: { layout: TableLayout; config: TableConfig }): Promise<UiViewDescriptor | undefined> {
    // If the full views cache is already populated, use it
    if (this.viewsPromise) {
      return (await this.viewsPromise).get(viewName)
    }
    // Build a single descriptor without loading all views — avoids calling api.table() for every view in the model
    const [table, formulas] = await Promise.all([
      prefetchedTable ? Promise.resolve(prefetchedTable) : this.api.table(viewName),
      this.formulas(),
    ])
    const formulaByName = new Map(formulas.map((f) => [f.name, f.formula]))
    return this.buildViewDescriptor(viewName, table, formulaByName)
  }

  /**
   * Return the canonical view name for a list-type element.
   * Given a type name (e.g. "Product"), finds the primary list via the engine,
   * then returns the name of the view whose homeTable matches that list.
   */
  async viewNameForListType(typeName: string): Promise<string | undefined> {
    try {
      const listName = await this.primaryListNameForType(typeName)
      const views = await this.views()
      return [...views.values()].find((v) => v.homeTable === listName)?.name
    } catch {
      return undefined
    }
  }

  /**
   * Return the canonical view name for a block/dataset element.
   * Given a dataset name (e.g. "COGS"), returns the name of the view whose
   * homeTable matches that dataset name (i.e. a single-dataset home view).
   */
  async viewNameForDataset(datasetName: string): Promise<string | undefined> {
    try {
      const views = await this.views()
      return [...views.values()].find((v) => v.homeTable === datasetName)?.name
    } catch {
      return undefined
    }
  }

  formulas() {
    if (!this.formulasPromise) {
      this.formulasPromise = this.api.formulas()
    }
    return this.formulasPromise
  }
}

export interface UiViewDescriptor {
  name: string
  layout: TableLayout
  config: TableConfig
  homeTable: string | null
  formula?: string
  customView: boolean
}
